import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useCelebrate } from "@/components/Celebration";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Progress } from "@/components/ui/progress";
import { DayList, WeekGrid, MonthGrid } from "@/components/planner/PlanViews";
import { ItemEditor } from "@/components/planner/ItemEditor";
import { PracticePanel } from "@/components/school/PracticePanel";
import { iso, parse, rangeFor, step, label } from "@/lib/planner";

const VIEWS = ["day", "week", "month"];

const LevelUpStrip = ({ tick = 0 }) => {
  const [s, setS] = useState([]);
  useEffect(() => { http.get("/levelup/status").then((r) => setS(r.data.filter((x) => x.level !== "above"))).catch(() => {}); }, [tick]);
  if (!s.length) return null;
  return (
    <div className="mt-6 flex flex-wrap gap-2" data-testid="levelup-strip">
      {s.map((x) => x.ready ? (
        <Link key={x.subject_id} to={`/level-up/${x.subject_id}`} data-testid={`levelup-ready-${x.subject_id}`} className="px-4 py-2 rounded-full bg-[#C87D53] text-white text-sm hover:scale-105 transition-transform">{x.subject}: Level-Up Challenge ready!</Link>
      ) : (
        <span key={x.subject_id} data-testid={`levelup-progress-${x.subject_id}`} className="px-4 py-2 rounded-full bg-[#F1EADB] text-stone-600 text-sm">{x.subject}: {x.reviews_done}/{x.needed} reviews to level up</span>
      ))}
    </div>
  );
};

export default function Planner() {
  const { studentId } = useParams();
  const { profile } = useProfile();
  const editable = !!studentId;
  const [view, setView] = useState("day");
  const [anchor, setAnchor] = useState(() => { const d = parse(iso(new Date())); return d.getDay() === 0 ? step("day", d, 1) : d.getDay() === 6 ? step("day", d, 1) : d; });
  const [d, setD] = useState(null);
  const celebrate = useCelebrate();
  const [tick, setTick] = useState(0);
  const [edit, setEdit] = useState({ open: false, item: null, date: null });
  const [start, end] = rangeFor(view, anchor);
  const load = useCallback(() => http.get(studentId ? `/planner/students/${studentId}` : "/planner", { params: { start: iso(start), end: iso(end) } })
    .then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e))), [studentId, view, anchor]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const synced = useRef(false);
  useEffect(() => {
    if (!d?.today || synced.current) return;
    synced.current = true;
    let t = parse(d.today);
    if (t.getDay() === 0 || t.getDay() === 6) t = step("day", t, 1);
    if (iso(t) !== iso(anchor)) setAnchor(t);
  }, [d?.today]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (item) => {
    setD((x) => ({ ...x, items: x.items.map((i) => (i.id === item.id ? { ...i, done: !i.done } : i)) }));
    try { const r = await http.patch(`/planner/items/${item.id}`, { done: !item.done }); if (r.data.celebrate) { celebrate(r.data.celebrate); setTick((t) => t + 1); } else if (!item.done) toast.success("Nice work! Checked off."); } catch (e) { toast.error(formatApiError(e)); load(); }
  };
  const del = async (item) => { if (!window.confirm(`Remove "${item.topic}" from the plan?`)) return; try { await http.delete(`/planner/students/${studentId}/items/${item.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const common = { editable, onToggle: toggle, onEdit: (item) => setEdit({ open: true, item }), onDelete: del };
  const name = d?.student?.name || profile?.name;
  const shown = d ? (view === "month" ? d.items.filter((i) => parse(i.date).getMonth() === anchor.getMonth()) : d.items) : [];
  const doneCount = shown.filter((i) => i.done).length;
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="planner-page">
      {editable && <div className="mb-4"><button onClick={() => window.history.back()} className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="planner-back"><ArrowLeft className="w-4 h-4" /> Back</button></div>}
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">{editable ? `Plan for ${name}` : "My plan"}</span>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C4A3E] mt-3">{editable ? `${name}'s assignments` : "What's on my plate"}</h1>
      <p className="mt-4 text-stone-600 max-w-2xl">{editable ? "A daily plan is created automatically across subjects. Add, move or change any assignment. Teacher lessons appear here too." : "Your assignments for the day, week and month. Check them off as you go. Finishing a lesson checks it off for you."}</p>
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="flex p-1 rounded-full bg-[#F1EADB]" data-testid="planner-view-toggle">
          {VIEWS.map((v) => <button key={v} onClick={() => setView(v)} data-testid={`planner-view-${v}`} className={`px-5 py-2 rounded-full text-sm capitalize transition-colors ${view === v ? "bg-[#2C4A3E] text-white" : "text-stone-600"}`}>{v}</button>)}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setAnchor(step(view, anchor, -1))} data-testid="planner-prev" className="p-2 rounded-full hover:bg-[#F1EADB]" aria-label="Previous"><ChevronLeft className="w-5 h-5" /></button>
          <span className="font-display text-xl text-[#2C4A3E] min-w-[220px] text-center" data-testid="planner-label">{label(view, anchor)}</span>
          <button onClick={() => setAnchor(step(view, anchor, 1))} data-testid="planner-next" className="p-2 rounded-full hover:bg-[#F1EADB]" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
        </div>
        <button onClick={() => setAnchor(parse(d?.today || iso(new Date())))} data-testid="planner-today" className="px-4 py-2 rounded-full border border-[#E6DEC3] text-sm">Today</button>
        {editable && <button onClick={() => setEdit({ open: true, item: null, date: iso(anchor) })} data-testid="planner-add-btn" className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C87D53] text-white text-sm"><Plus className="w-4 h-4" /> Add assignment</button>}
      </div>
      {!editable && <LevelUpStrip tick={tick} />}
      <PracticePanel studentId={studentId || "me"} onChange={load} />
      {d && (
        <div className="mt-6 flex items-center gap-3 max-w-md" data-testid="planner-progress">
          <Progress value={shown.length ? (100 * doneCount) / shown.length : 0} className="h-2.5" />
          <span className="text-sm text-[#2C4A3E] whitespace-nowrap" data-testid="planner-progress-text">{doneCount}/{shown.length} done</span>
        </div>
      )}
      <div className="mt-8">
        {!d ? <p className="text-stone-500">Loading…</p> : view === "day" ? <DayList items={d.items} {...common} />
          : view === "week" ? <WeekGrid start={start} items={d.items} today={d.today} onAdd={(date) => setEdit({ open: true, item: null, date })} {...common} />
          : <MonthGrid start={start} end={end} month={anchor.getMonth()} items={d.items} today={d.today} onPick={(day) => { setAnchor(day); setView("day"); }} />}
      </div>
      {editable && <ItemEditor studentId={studentId} item={edit.item} open={edit.open} setOpen={(o) => setEdit((x) => ({ ...x, open: o }))} defaultDate={edit.date} onSaved={load} />}
      {!editable && <p className="mt-10 text-xs text-stone-400">Want to explore more? <Link to="/" className="underline">Browse all subjects</Link></p>}
    </div>
  );
}
